import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  OfflineDownloadStatusStore,
  OfflineDownloadStatusStoreData,
} from './types/offlineDownloadStatus.types';
import { getOfflineDownloadStatusDBStorage } from './utils/getOfflineDownloadStatusDBStorage';
import { filterOutKeys } from './utils/offlineDownloadStatus.utils';

const PERSISTER_KEY = 'offline-download-status';

export const getOfflineDownloadStatusStoreBasedOnTeam = (teamId?: string) =>
  create<OfflineDownloadStatusStore>()(
    persist(
      (set) => ({
        cachedData: undefined,
        isUpdatingData: false,

        initDownload: ({ teamId }) => {
          set((state) => {
            state.cachedData = {
              teamId,
              allKeys: [],
              cachedKeys: [],
              lastSyncedAt: new Date(),
            };

            return state;
          });
        },
        finishedDownload: () => {
          set((state) => {
            if (!state.cachedData) {
              return state;
            }

            const newState = {
              ...state,
              cachedData: {
                ...state.cachedData,
                finishedDownloadAt: new Date(),
              },
            };

            return newState;
          });
        },
        addAllKeys: ({ keys }) => {
          set((state) => {
            if (!state.cachedData) {
              return state;
            }

            const newState = {
              ...state,
              cachedData: {
                ...state.cachedData,
                allKeys: keys,
              },
            };

            return newState;
          });
        },
        handleAddKeyToUpdateFlow: ({ keys }) => {
          set((state) => {
            if (!state.cachedData) {
              return state;
            }

            const allKeysWithoutNew = filterOutKeys(state.cachedData.allKeys, keys);
            const allCachedKeysWithoutNew = filterOutKeys(state.cachedData.cachedKeys, keys);

            const newState = {
              ...state,
              cachedData: {
                ...state.cachedData,
                cachedKeys: allCachedKeysWithoutNew,
                allKeys: [...allKeysWithoutNew, ...keys],
              },
            };

            return newState;
          });
        },
        updateCachedKeys: ({ keys }) => {
          set((state) => {
            if (!state.cachedData) {
              return state;
            }

            const newState = {
              ...state,
              cachedData: {
                ...state.cachedData,
                cachedKeys: [...state.cachedData.cachedKeys, ...keys],
              },
            };

            return newState;
          });
        },
        removeCachedData: () => {
          set((state) => {
            delete state.cachedData;

            return { ...state };
          });
        },
        initUpdatingData: () => {
          set((state) => ({
            ...state,
            isUpdatingData: true,
          }));
        },
        finishedUpdatingData: () => {
          set((state) => ({
            ...state,
            isUpdatingData: false,
          }));
        },
      }),
      {
        version: 0,
        name: PERSISTER_KEY,
        skipHydration: true,
        storage: getOfflineDownloadStatusDBStorage(teamId),
        partialize: (state) => ({
          cachedData: state.cachedData,
        }),
        merge: (persisted, current) => {
          const typedPersisted = persisted as OfflineDownloadStatusStoreData;

          return {
            ...current,
            cachedData: typedPersisted?.cachedData,
          };
        },
      },
    ),
  );

export const useOfflineDownloadStatusStore = getOfflineDownloadStatusStoreBasedOnTeam();
